import { isBefore, startOfDay } from "date-fns"
import { t } from "i18next"
import { z } from "zod"
import { textAreaDefaultMaxLength } from "@/models/common"
import { Medication, Substance } from "@/modules/patients/detail/medication/de/search-results"
import { MedicationUnit } from "@/modules/patients/detail/medication/de/services/__generated__/useDeCreateMedicationMutation.graphql"

export type Dosage = "morningDosage" | "noonDosage" | "eveningDosage" | "nightDosage"
export type DeMedicationField = "medication" | "substance" | "freeTextMedicationName"

export const medicationUsageInstructionMaxLength = 200

export const MedicationSchema = z
  .object({
    morningDosage: z.string(),
    noonDosage: z.string(),
    eveningDosage: z.string(),
    nightDosage: z.string(),
    unit: z.custom<MedicationUnit>(),
    usageInstruction: z
      .string()
      .max(medicationUsageInstructionMaxLength, { message: t("common.fieldErrorList.characterLimit") }),
    internalNote: z.string().max(textAreaDefaultMaxLength, { message: t("common.fieldErrorList.characterLimit") }),
    medication: z.custom<Medication>().nullish(),
    substance: z.custom<Substance>().nullish(),
    refuseSubstitutes: z.boolean().optional().default(false),
    startDate: z.date().optional(),
    endDate: z.date().optional(),
    pharmaceuticalCentralNumber: z.string().optional(),
    freeTextMedicationName: z.string().trim().max(250, { message: "common.fieldErrorList.characterLimit" }).nullable(),
    numberOfPackages: z.number().min(1).max(9).default(1),
    asNeeded: z.boolean().optional(),
    isPermanentPrescription: z.boolean().optional(),
  })
  .superRefine(({ medication, substance, startDate, endDate, freeTextMedicationName, asNeeded, ...rest }, ctx) => {
    if (!medication && !substance && !freeTextMedicationName) {
      ctx.addIssue({
        code: "custom",
        path: ["medication"],
        message: "common.fieldErrorList.requiredField",
      })
      ctx.addIssue({
        code: "custom",
        path: ["freeTextMedicationName"],
        message: "common.fieldErrorList.requiredField",
      })
    }
    if (startDate && endDate && isBefore(startOfDay(endDate), startOfDay(startDate))) {
      ctx.addIssue({
        code: "custom",
        path: ["startDate"],
        message: "common.fieldErrorList.invalidDateRange",
      })
      ctx.addIssue({
        code: "custom",
        path: ["endDate"],
        message: "common.fieldErrorList.invalidDateRange",
      })
    }
    if (!asNeeded) {
      const dosages: Array<Dosage> = ["morningDosage", "noonDosage", "eveningDosage", "nightDosage"]
      dosages.forEach((dosage) => {
        if (!rest[dosage]) {
          ctx.addIssue({
            code: "custom",
            path: [dosage],
            message: "common.fieldErrorList.requiredField",
          })
        }
      })
    }
  })

export type MedicationSchemaType = z.infer<typeof MedicationSchema>
export type MedicationSchemaInput = z.input<typeof MedicationSchema>
